/**
 * Column definitions shared by the alliance roster and the state player list.
 *
 * Every numeric cell goes through `Qualified`, so a `vip: 0` that Jeabs never
 * enriched is visibly different from a confirmed VIP 0 (spec section 56) even
 * inside a dense table.
 *
 * Exposed as a hook rather than a plain function because the headers and the
 * field names inside each tooltip are translated, and the columns therefore
 * have to be rebuilt when the locale changes.
 */

import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { PlayerSummary } from '@kso/schemas';
import { Qualified } from '../../components/quality/Qualified';
import { FreshnessBadge } from '../../components/freshness/Freshness';
import { Value } from '../../components/ui/primitives';
import { formatCompact, formatNumber } from '../../lib/format';
import { useT } from '../../i18n';

/**
 * Sorts nulls last in both directions.
 *
 * The default would treat "we have never measured this player's power" as
 * zero and rank them below everyone, which reads as a fact about the player
 * rather than a gap in our data.
 */
function nullsLast(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

export function usePlayerColumns(
  options: { showAlliance?: boolean } = {},
): Array<ColumnDef<PlayerSummary, unknown>> {
  const t = useT();
  const showAlliance = options.showAlliance ?? false;

  return useMemo(() => {
    const columns: Array<ColumnDef<PlayerSummary, unknown>> = [
      {
        id: 'name',
        header: t.common.player,
        accessorFn: (row) => row.name ?? '',
        cell: ({ row }) => (
          <Link to={`/players/${encodeURIComponent(row.original.id)}`}>
            <Value absent={t.common.unnamedPlayer}>{row.original.name}</Value>
          </Link>
        ),
      },
      {
        id: 'townHallLevel',
        header: t.common.tc,
        accessorFn: (row) => row.townHallLevel,
        sortingFn: (a, b) => nullsLast(a.original.townHallLevel, b.original.townHallLevel),
        cell: ({ row }) => (
          <span className="num">
            <Qualified
              field={t.common.townCentreLevel}
              quality={row.original.quality['townHallLevel']}
              value={row.original.townHallLevel}
            />
          </span>
        ),
      },
      {
        id: 'power',
        header: t.common.power,
        accessorFn: (row) => row.power,
        sortingFn: (a, b) => nullsLast(a.original.power, b.original.power),
        cell: ({ row }) => (
          <span className="num">
            <Qualified
              field={t.common.power}
              quality={row.original.quality['power']}
              value={formatCompact(row.original.power)}
            />
          </span>
        ),
      },
      {
        id: 'kills',
        header: t.common.kills,
        accessorFn: (row) => row.kills,
        sortingFn: (a, b) => nullsLast(a.original.kills, b.original.kills),
        cell: ({ row }) => (
          <span className="num">
            <Qualified
              field={t.common.kills}
              quality={row.original.quality['kills']}
              value={formatCompact(row.original.kills)}
            />
          </span>
        ),
      },
      {
        id: 'vip',
        header: t.common.vip,
        accessorFn: (row) => row.vip,
        sortingFn: (a, b) => nullsLast(a.original.vip, b.original.vip),
        cell: ({ row }) => (
          <span className="num">
            <Qualified
              field={t.common.vip}
              quality={row.original.quality['vip']}
              value={row.original.vip}
            />
          </span>
        ),
      },
      {
        id: 'level',
        header: t.common.level,
        accessorFn: (row) => row.level,
        sortingFn: (a, b) => nullsLast(a.original.level, b.original.level),
        cell: ({ row }) => (
          <span className="num">
            <Qualified
              field={t.common.level}
              quality={row.original.quality['level']}
              value={formatNumber(row.original.level)}
            />
          </span>
        ),
      },
    ];

    if (showAlliance) {
      columns.push({
        id: 'alliance',
        header: t.common.alliance,
        accessorFn: (row) => row.allianceAbbr ?? row.allianceName ?? '',
        cell: ({ row }) =>
          row.original.allianceId ? (
            <Link to={`/state/alliances?focus=${encodeURIComponent(row.original.allianceId)}`}>
              {row.original.allianceAbbr ?? row.original.allianceName ?? row.original.allianceId}
            </Link>
          ) : (
            <span className="absent">{t.common.noAlliance}</span>
          ),
      });
    }

    columns.push({
      id: 'freshness',
      header: t.player.columns.profileAge,
      enableSorting: false,
      cell: ({ row }) => <FreshnessBadge label="" info={row.original.freshness.profile} />,
    });

    return columns;
  }, [t, showAlliance]);
}
